import { ensureCaseDbCsrfToken } from '../utils/csrf';
import { caseDbApiEndpoint } from '../utils/apiOrigin';

// Numbered archive deliveries are a permanent DocumentSection capability.
// The choice modal still lets the user keep the parts as ordinary files.
export const ARCHIVE_IMPORT_ENABLED = true;

const DEFAULT_ENDPOINT = caseDbApiEndpoint('case-archive-import.php');
const DEFAULT_UPLOAD_CONCURRENCY = 4;
const MAX_PART_RETRIES = 3;
const IDEMPOTENT_REQUEST_RETRIES = 3;
const ARCHIVE_RESUME_PREFIX = 'casedb.archive-import.v1.';
const ARCHIVE_RESUME_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const ARCHIVE_UPLOAD_CHUNK_TIMEOUT_MS = 30 * 60 * 1000;

export class ArchiveImportError extends Error {
  constructor(message, status = 0, code = '') {
    super(message);
    this.name = 'ArchiveImportError';
    this.status = status;
    this.code = code;
  }
}

const responsePayload = async (response) => {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new ArchiveImportError(
      response.ok
        ? 'CaseDB returned an unreadable archive-import response.'
        : `Archive import failed (${response.status}${response.statusText ? ` ${response.statusText}` : ''}).`,
      response.status,
      'invalid_response',
    );
  }
};

const randomRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `archive-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const defaultResumeStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const stableResumeHash = (value) => {
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193) >>> 0;
    right = Math.imul(right ^ code, 0x85ebca6b) >>> 0;
  }
  return `${left.toString(16).padStart(8, '0')}${right.toString(16).padStart(8, '0')}`;
};

export const archiveResumeFingerprint = ({
  caseId,
  destinationPath = '{{Name}}',
  baseName,
  importMode = 'prepare',
  password = '',
}, manifest) => JSON.stringify({
  case_id: String(caseId || ''),
  destination_path: String(destinationPath || '{{Name}}'),
  base_name: String(baseName || ''),
  import_mode: String(importMode || 'prepare'),
  password_provided: Boolean(password),
  sources: manifest.map(({ file, name, size, part_number: partNumber }) => ({
    name,
    size,
    part_number: partNumber,
    last_modified: Number(file?.lastModified || 0),
  })),
});

const normalizedEtag = (value) => String(value || '').trim();
const VALID_SHA256_PATTERN = /^[a-f0-9]{64}$/;

const browserSha256 = async (blob) => {
  const browserCrypto = typeof crypto !== 'undefined' ? crypto : null;
  if (!browserCrypto?.subtle || typeof blob?.arrayBuffer !== 'function') {
    throw new ArchiveImportError(
      'This browser cannot safely resume a document-package upload.',
      0,
      'archive_resume_digest_unavailable',
    );
  }
  const digest = await browserCrypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const browserCanDigest = () => Boolean(typeof crypto !== 'undefined' && crypto.subtle);

const waitForRetry = (delayMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(new DOMException('The archive upload was cancelled.', 'AbortError'));
    return;
  }
  let timer = null;
  const handleAbort = () => {
    clearTimeout(timer);
    reject(new DOMException('The archive upload was cancelled.', 'AbortError'));
  };
  timer = setTimeout(() => {
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  }, delayMs);
  signal?.addEventListener('abort', handleAbort, { once: true });
});

/**
 * Detect the order of common split-archive names. A normal, unsplit archive is
 * part 1. The UI owns the decision to offer archive extraction; this helper
 * only creates a stable upload manifest once the user accepts it.
 */
export const archivePartNumberFromName = (name, fallback = 1) => {
  const normalized = String(name || '').trim();
  const numberedZip = normalized.match(/\.zip\.(\d{3,6})$/i);
  if (numberedZip) return Number(numberedZip[1]);
  const zSegment = normalized.match(/\.z(\d{2,5})$/i);
  if (zSegment) return Number(zSegment[1]);
  const numericSegment = normalized.match(/\.(\d{3,6})$/);
  if (numericSegment) return Number(numericSegment[1]);
  return fallback;
};

export const buildArchiveSourceManifest = (files) => {
  const list = Array.from(files || []);
  return list
    .map((file, index) => ({
      file,
      name: file?.name || `archive-part-${index + 1}`,
      size: Number(file?.size || 0),
      content_type: file?.type || 'application/octet-stream',
      part_number: archivePartNumberFromName(file?.name, index + 1),
    }))
    .sort((left, right) => left.part_number - right.part_number || left.name.localeCompare(right.name));
};

const xhrPut = ({ url, blob, signal, onProgress }) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  let abortListener = null;

  const cleanup = () => {
    if (signal && abortListener) signal.removeEventListener('abort', abortListener);
  };

  xhr.open('PUT', url, true);
  xhr.timeout = ARCHIVE_UPLOAD_CHUNK_TIMEOUT_MS;
  xhr.upload.addEventListener('progress', (event) => {
    if (event.lengthComputable && onProgress) onProgress(event.loaded, event.total);
  });
  xhr.addEventListener('load', () => {
    cleanup();
    if (xhr.status < 200 || xhr.status >= 300) {
      reject(new ArchiveImportError(`S3 rejected an archive chunk (${xhr.status}).`, xhr.status, 's3_upload_failed'));
      return;
    }
    const etag = normalizedEtag(xhr.getResponseHeader('ETag'));
    resolve(etag);
  });
  xhr.addEventListener('error', () => {
    cleanup();
    reject(new ArchiveImportError('The archive chunk could not reach S3.', 0, 's3_network_error'));
  });
  xhr.addEventListener('timeout', () => {
    cleanup();
    reject(new ArchiveImportError(
      'An archive chunk stopped making progress. CaseDB will retry it.',
      0,
      's3_upload_timeout',
    ));
  });
  xhr.addEventListener('abort', () => {
    cleanup();
    reject(new DOMException('The archive upload was cancelled.', 'AbortError'));
  });

  if (signal) {
    abortListener = () => xhr.abort();
    if (signal.aborted) {
      cleanup();
      reject(new DOMException('The archive upload was cancelled.', 'AbortError'));
      return;
    }
    signal.addEventListener('abort', abortListener, { once: true });
  }
  xhr.send(blob);
});

export class ArchiveImportService {
  constructor({
    endpoint = DEFAULT_ENDPOINT,
    uploadConcurrency = DEFAULT_UPLOAD_CONCURRENCY,
    resumeStorage = defaultResumeStorage(),
    digestBlob,
  } = {}) {
    this.endpoint = endpoint;
    this.uploadConcurrency = Math.max(1, Math.min(8, Number(uploadConcurrency) || DEFAULT_UPLOAD_CONCURRENCY));
    this.resumeStorage = resumeStorage;
    this.digestBlob = typeof digestBlob === 'function'
      ? digestBlob
      : (browserCanDigest() ? browserSha256 : null);
  }

  resumeEntry(options, manifest) {
    const signature = archiveResumeFingerprint(options, manifest);
    const storageKey = `${ARCHIVE_RESUME_PREFIX}${stableResumeHash(signature)}`;
    // A password must never be represented by a reusable local fingerprint.
    // Starting an encrypted upload therefore creates a fresh server envelope;
    // interrupted password-assisted uploads restart rather than silently
    // replaying a job encrypted with an older password.
    const persistent = Boolean(this.resumeStorage && this.digestBlob && !options.password);
    let requestId = options.password ? '' : (options.requestId || '');
    let storedEntry = null;
    if (!requestId && persistent) {
      try {
        const stored = JSON.parse(this.resumeStorage.getItem(storageKey) || 'null');
        const stillCurrent = stored
          && stored.signature === signature
          && typeof stored.request_id === 'string'
          && (Date.now() - Number(stored.created_at || 0)) <= ARCHIVE_RESUME_MAX_AGE_MS;
        if (stillCurrent) {
          requestId = stored.request_id;
          storedEntry = stored;
        }
      } catch (error) {
        // Private browsing and locked-down browsers may disable storage.
      }
    }
    requestId = requestId || randomRequestId();
    const state = {
      signature,
      request_id: requestId,
      created_at: Number(storedEntry?.created_at || Date.now()),
      part_sha256: storedEntry?.part_sha256 && typeof storedEntry.part_sha256 === 'object'
        ? storedEntry.part_sha256
        : {},
    };
    if (persistent) {
      try {
        this.resumeStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        // Uploading still works; it simply cannot resume after a page close.
      }
    }
    return { persistent, requestId, state, storageKey: persistent ? storageKey : '' };
  }

  persistResumeEntry(resume) {
    if (!resume?.persistent || !resume.storageKey || !this.resumeStorage) return;
    try {
      this.resumeStorage.setItem(resume.storageKey, JSON.stringify(resume.state));
    } catch (error) {
      // Current uploading is unaffected. A later page cannot safely reuse a
      // chunk whose digest was not durably recorded, so it will upload again.
    }
  }

  rememberedPartDigest(resume, sourcePartNumber, multipartPartNumber) {
    const value = resume?.state?.part_sha256?.[String(sourcePartNumber)]?.[String(multipartPartNumber)];
    return VALID_SHA256_PATTERN.test(String(value || '')) ? String(value) : '';
  }

  rememberPartDigest(resume, sourcePartNumber, multipartPartNumber, digest) {
    if (!resume?.persistent || !VALID_SHA256_PATTERN.test(String(digest || ''))) return;
    const sourceKey = String(sourcePartNumber);
    const partKey = String(multipartPartNumber);
    if (!resume.state.part_sha256[sourceKey] || typeof resume.state.part_sha256[sourceKey] !== 'object') {
      resume.state.part_sha256[sourceKey] = {};
    }
    resume.state.part_sha256[sourceKey][partKey] = digest;
    this.persistResumeEntry(resume);
  }

  async localPartMatchesResume({ file, resume, sourcePartNumber, partNumber, chunkSize }) {
    if (!resume?.persistent || !this.digestBlob) return false;
    const remembered = this.rememberedPartDigest(resume, sourcePartNumber, partNumber);
    if (!remembered) return false;
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const current = await this.digestBlob(file.slice(start, end));
    return VALID_SHA256_PATTERN.test(String(current || '')) && current === remembered;
  }

  clearResumeEntry(storageKey) {
    if (!storageKey || !this.resumeStorage) return;
    try {
      this.resumeStorage.removeItem(storageKey);
    } catch (error) {
      // The durable server job is already queued; stale local state is harmless.
    }
  }

  async request(action, payload = {}, { method = 'POST', signal, allowFailurePayload = false } = {}) {
    let csrfToken = '';
    if (method !== 'GET') {
      try {
        csrfToken = await ensureCaseDbCsrfToken();
      } catch (error) {
        // The protected endpoint will provide the definitive auth/CSRF error.
      }
    }

    const url = new URL(this.endpoint, typeof window !== 'undefined' ? window.location.href : undefined);
    const options = { method, credentials: 'include', signal };
    if (method === 'GET') {
      url.searchParams.set('action', action);
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
      });
    } else {
      options.headers = {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      };
      options.body = JSON.stringify({ action, ...payload });
    }

    let response;
    try {
      response = await fetch(url.toString(), options);
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      throw new ArchiveImportError(
        'CaseDB could not reach document preparation. Please try again in a moment.',
        0,
        'archive_service_unavailable',
      );
    }
    const result = await responsePayload(response);
    if (!response.ok || (result.success === false && !allowFailurePayload)) {
      throw new ArchiveImportError(
        result.message || result.error || `Archive import failed (${response.status}).`,
        response.status,
        result.error_code || result.code || '',
      );
    }
    return result;
  }

  async retryIdempotentRequest(action, payload, { signal } = {}) {
    let lastError = null;
    for (let attempt = 1; attempt <= IDEMPOTENT_REQUEST_RETRIES; attempt += 1) {
      try {
        return await this.request(action, payload, { signal });
      } catch (error) {
        lastError = error;
        const retryable = !error?.status || error.status >= 500;
        if (error?.name === 'AbortError' || !retryable || attempt >= IDEMPOTENT_REQUEST_RETRIES) {
          throw error;
        }
        await waitForRetry(250 * attempt, signal);
      }
    }
    throw lastError;
  }

  async create({
    caseId,
    destinationPath = '{{Name}}',
    baseName,
    files,
    requestId,
    importMode = 'prepare',
    password = '',
    signal,
  }) {
    const manifest = buildArchiveSourceManifest(files);
    if (manifest.length === 0) throw new ArchiveImportError('Select at least one archive file.', 0, 'no_sources');
    if (manifest.some((item) => !Number.isSafeInteger(item.size) || item.size <= 0)) {
      throw new ArchiveImportError('Every archive part must be a non-empty file.', 0, 'invalid_source_size');
    }

    const createPayload = {
      case_id: caseId,
      destination_path: destinationPath,
      base_name: baseName,
      import_mode: importMode,
      ...(importMode === 'prepare' && password ? { password } : {}),
      request_id: requestId || randomRequestId(),
      sources: manifest.map(({ name, size, content_type: contentType, part_number: partNumber }) => ({
        name,
        size,
        content_type: contentType,
        part_number: partNumber,
      })),
    };

    let result;
    let lastError = null;
    for (let attempt = 1; attempt <= IDEMPOTENT_REQUEST_RETRIES; attempt += 1) {
      try {
        result = await this.request('create', createPayload, { signal });
        break;
      } catch (error) {
        lastError = error;
        const createStillStarting = error?.code === 'archive_create_busy';
        const retryable = !error?.status || error.status >= 500 || createStillStarting;
        if (error?.name === 'AbortError' || !retryable || attempt >= IDEMPOTENT_REQUEST_RETRIES) {
          throw error;
        }
        // The first response may have been lost after CaseDB started creating
        // the job. Reusing this request ID lets the next attempt discover that
        // same job after the short create gate is released.
        await waitForRetry(createStillStarting ? 1500 * attempt : 250 * attempt, signal);
      }
    }

    if (!result) throw lastError || new ArchiveImportError('The archive upload could not be prepared.');

    return { ...result, manifest };
  }

  status(jobId, { signal } = {}) {
    // A terminal failed/cancelled job is a successful status lookup whose
    // payload intentionally has success=false. Return that state to the tray
    // so it can stop polling and refresh the originals preserved in the case.
    return this.request(
      'status',
      { job_id: jobId },
      { method: 'GET', signal, allowFailurePayload: true },
    );
  }

  retryWithPassword({ jobId, retryToken, password, signal } = {}) {
    if (typeof jobId !== 'string' || !jobId.trim()
      || typeof retryToken !== 'string' || !retryToken.trim()
      || typeof password !== 'string' || password.length === 0) {
      throw new ArchiveImportError(
        'Enter the archive password and try again.',
        0,
        'archive_password_retry_invalid',
      );
    }

    return this.request('retry_with_password', {
      job_id: jobId,
      password_retry_token: retryToken,
      // The password exists only in this authenticated request body. It is
      // never added to resumable upload state, browser storage, URLs, or logs.
      password,
    }, { signal });
  }

  cancel(jobId, { signal } = {}) {
    return this.request(
      'cancel',
      { job_id: jobId },
      { signal, allowFailurePayload: true },
    );
  }

  finalize(jobId, { signal } = {}) {
    return this.retryIdempotentRequest('finalize', { job_id: jobId }, { signal });
  }

  async signPart(jobId, sourceId, partNumber, size, { signal } = {}) {
    return this.request('sign_part', {
      job_id: jobId,
      source_id: sourceId,
      multipart_part_number: partNumber,
      size,
    }, { signal });
  }

  listUploadedParts(jobId, sourceId, { signal } = {}) {
    return this.request('list_parts', {
      job_id: jobId,
      source_id: sourceId,
    }, { method: 'GET', signal });
  }

  async completeUpload(jobId, sourceId, parts, { signal } = {}) {
    return this.retryIdempotentRequest('complete_upload', {
      job_id: jobId,
      source_id: sourceId,
      parts,
    }, { signal });
  }

  async uploadChunk({ jobId, sourceId, partNumber, blob, signal, onProgress }) {
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_PART_RETRIES; attempt += 1) {
      try {
        const signed = await this.signPart(jobId, sourceId, partNumber, blob.size, { signal });
        const etag = await xhrPut({ url: signed.upload_url, blob, signal, onProgress });
        return { part_number: partNumber, ...(etag ? { etag } : {}), size: blob.size };
      } catch (error) {
        lastError = error;
        if (error?.name === 'AbortError' || attempt >= MAX_PART_RETRIES) throw error;
      }
    }
    throw lastError;
  }

  async uploadSource({ jobId, source, file, uploadPlan, resume, signal, onProgress }) {
    const sourceId = source.source_id || source.client_file_id;
    const chunkSize = Number(uploadPlan?.chunk_size || 0);
    const chunkCount = Number(uploadPlan?.chunk_count || 0);
    if (!sourceId || !Number.isSafeInteger(chunkSize) || chunkSize <= 0 || !Number.isSafeInteger(chunkCount) || chunkCount <= 0) {
      throw new ArchiveImportError('CaseDB returned an invalid archive upload plan.', 0, 'invalid_upload_plan');
    }
    if (Number(source.expected_size) !== file.size) {
      throw new ArchiveImportError(`“${file.name}” changed after the upload was prepared.`, 0, 'source_size_changed');
    }

    const completed = new Array(chunkCount);
    const loadedByPart = new Map();
    const report = () => {
      if (!onProgress) return;
      const loaded = Array.from(loadedByPart.values()).reduce((sum, value) => sum + value, 0);
      onProgress({ sourceId, file, loaded, total: file.size });
    };

    const uploaded = await this.listUploadedParts(jobId, sourceId, { signal });
    const seen = new Set();
    const reusableParts = [];
    (Array.isArray(uploaded?.parts) ? uploaded.parts : []).forEach((part) => {
      const partNumber = Number(part?.part_number);
      const size = Number(part?.size);
      const expectedSize = partNumber >= 1 && partNumber <= chunkCount
        ? Math.min(chunkSize, file.size - ((partNumber - 1) * chunkSize))
        : 0;
      if (!Number.isSafeInteger(partNumber) || seen.has(partNumber)
        || !Number.isSafeInteger(size) || expectedSize <= 0 || size !== expectedSize) {
        return;
      }
      seen.add(partNumber);
      reusableParts.push({ partNumber, size });
    });
    seen.clear();
    for (const { partNumber, size } of reusableParts) {
      if (await this.localPartMatchesResume({
        file,
        resume,
        sourcePartNumber: source.part_number,
        partNumber,
        chunkSize,
      })) {
        seen.add(partNumber);
        completed[partNumber - 1] = { part_number: partNumber, size };
        loadedByPart.set(partNumber, size);
      }
    }
    const queue = Array.from({ length: chunkCount }, (_, index) => index + 1)
      .filter((partNumber) => !seen.has(partNumber));
    report();

    const worker = async () => {
      while (queue.length > 0) {
        if (signal?.aborted) throw new DOMException('The archive upload was cancelled.', 'AbortError');
        const partNumber = queue.shift();
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        const digest = resume?.persistent && this.digestBlob
          ? await this.digestBlob(blob)
          : '';
        completed[partNumber - 1] = await this.uploadChunk({
          jobId,
          sourceId,
          partNumber,
          blob,
          signal,
          onProgress: (loaded) => {
            loadedByPart.set(partNumber, loaded);
            report();
          },
        });
        this.rememberPartDigest(resume, source.part_number, partNumber, digest);
        loadedByPart.set(partNumber, blob.size);
        report();
      }
    };

    await Promise.all(Array.from({ length: Math.min(this.uploadConcurrency, queue.length) }, worker));
    return this.completeUpload(jobId, sourceId, completed, { signal });
  }

  async uploadedSourceMatchesLocalFile({ source, file, uploadPlan, resume }) {
    const chunkSize = Number(uploadPlan?.chunk_size || 0);
    const chunkCount = Number(uploadPlan?.chunk_count || 0);
    if (!Number.isSafeInteger(chunkSize) || chunkSize <= 0
      || !Number.isSafeInteger(chunkCount) || chunkCount <= 0
      || Number(source.expected_size) !== file.size) {
      return false;
    }
    for (let partNumber = 1; partNumber <= chunkCount; partNumber += 1) {
      if (!await this.localPartMatchesResume({
        file,
        resume,
        sourcePartNumber: source.part_number,
        partNumber,
        chunkSize,
      })) return false;
    }
    return true;
  }

  /** Create, upload, and queue an import. Extraction happens asynchronously. */
  async start(options) {
    const localManifest = buildArchiveSourceManifest(options.files);
    const resume = this.resumeEntry(options, localManifest);
    let created;
    try {
      created = await this.create({ ...options, requestId: resume.requestId });
    } catch (error) {
      if (['archive_request_conflict', 'archive_not_queueable', 'archive_upload_closed'].includes(error?.code)) {
        this.clearResumeEntry(resume.storageKey);
      }
      throw error;
    }
    const { job, sources = [], upload_plans: uploadPlans = {}, manifest } = created;
    const jobId = job?.job_id || job?.public_id || created.job_id;
    if (!jobId) throw new ArchiveImportError('CaseDB did not return an archive job ID.', 0, 'missing_job_id');

    const filesByPart = new Map(manifest.map((item) => [item.part_number, item.file]));
    try {
      for (const source of sources) {
        const partNumber = Number(source.part_number);
        const file = filesByPart.get(partNumber);
        if (!file) throw new ArchiveImportError(`Archive part ${partNumber} is no longer selected.`, 0, 'missing_local_source');
        if (source.uploaded) {
          const sourceId = source.source_id || source.client_file_id;
          const matches = await this.uploadedSourceMatchesLocalFile({
            source,
            file,
            uploadPlan: uploadPlans[sourceId],
            resume,
          });
          if (!matches) {
            this.clearResumeEntry(resume.storageKey);
            throw new ArchiveImportError(
              `“${file.name}” does not match the interrupted upload. Choose Upload again to start a fresh copy.`,
              0,
              'archive_resume_file_mismatch',
            );
          }
          options.onProgress?.({
            sourceId: source.source_id || source.client_file_id,
            file,
            loaded: file.size,
            total: file.size,
          });
          continue;
        }
        const sourceId = source.source_id || source.client_file_id;
        await this.uploadSource({
          jobId,
          source,
          file,
          uploadPlan: uploadPlans[sourceId],
          resume,
          signal: options.signal,
          onProgress: options.onProgress,
        });
      }
    } catch (error) {
      if ([
        'archive_not_queueable',
        'archive_upload_closed',
        'archive_multipart_unavailable',
        'source_size_mismatch',
      ].includes(error?.code)) {
        this.clearResumeEntry(resume.storageKey);
      }
      throw error;
    }

    try {
      const finalized = await this.finalize(jobId, { signal: options.signal });
      this.clearResumeEntry(resume.storageKey);
      return finalized;
    } catch (error) {
      if (['archive_not_queueable', 'archive_upload_closed'].includes(error?.code)) {
        this.clearResumeEntry(resume.storageKey);
      }
      throw error;
    }
  }
}

export const archiveImportService = new ArchiveImportService();
export default archiveImportService;
